/**
 * `<count-up>` (sections/numbers.liquid): counts every `[data-count-to]` figure inside it up from 0
 * once the element is a third in view: 1.8 s each, ease-out, 160 ms apart, as drawn in
 * Numbers.dc.html. Formatted for the page's language. Nothing moves meanwhile: each figure sits over
 * an invisible copy of its final value that holds the width.
 *
 * No animation with reduced motion or `data-still` (the section's "Count up" off): the final
 * figures rendered by Liquid stay as they are.
 */

const DURATION = 1800;
const STAGGER = 160;
const ease = (x) => 1 - (1 - x) ** 4;

class CountUp extends HTMLElement {
  #observer = null;
  #frame = 0;

  connectedCallback() {
    if (this.hasAttribute("data-still") || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const format = new Intl.NumberFormat(document.documentElement.lang || undefined);
    const figures = [...this.querySelectorAll("[data-count-to]")];
    if (!figures.length) return;
    for (const figure of figures) figure.textContent = format.format(0);

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.#observer?.disconnect();
        this.#run(figures, format);
      },
      { threshold: 0.35 },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #run(figures, format) {
    const start = performance.now();
    const tick = (now) => {
      let running = false;
      figures.forEach((figure, index) => {
        const target = Number(figure.dataset.countTo) || 0;
        const progress = Math.min(1, Math.max(0, (now - start - index * STAGGER) / DURATION));
        if (progress < 1) running = true;
        figure.textContent = format.format(Math.round(target * ease(progress)));
      });
      if (running) this.#frame = requestAnimationFrame(tick);
    };
    this.#frame = requestAnimationFrame(tick);
  }
}

customElements.define("count-up", CountUp);
